import React, { useEffect, useState, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Polyline, GeoJSON, useMap } from 'react-leaflet';
import HeatLayer from './HeatLayer';
import { formatKm2 } from '../utils/basicStyles';
import { Search, X, RectangleHorizontalIcon, Ruler, LayoutGrid, MapPin, ArrowRight, Minus, Lock, Unlock, Flame } from 'lucide-react';
import { FORMATS } from '../config/prices';
import EditableRectangle from './EditableRectangle';
import L from 'leaflet';
import { getActivityTypeColor } from '../utils/activityColor';
import { Button } from "./button"

const HIGHLIGHT_COLOR = '#ff3b3b';

const MapController = ({ rectangleBounds, shouldZoomToRectangle, setShouldZoomToRectangle }) => {
  const map = useMap();
  useEffect(() => {
    if (shouldZoomToRectangle && rectangleBounds) {
      const bounds = L.latLngBounds(rectangleBounds);
      map.fitBounds(bounds, { padding: [50, 50] });
      setTimeout(() => setShouldZoomToRectangle(false), 0);
    }
  }, [shouldZoomToRectangle]);

  return null;
};

// Imperatively fit the map to the rectangle / a selected activity's polyline.
// Child of MapContainer so it has access to the leaflet map instance via
// useMap; the parent signals via a nonce that increments on each click.
// Notifies the parent whenever the leaflet map's zoom changes, so Dashboard
// can persist the current zoom and restore it across reloads. Uses a ref
// for the callback so we don't rebind the leaflet listener every render.
const ZoomListener = ({ onZoomChange }) => {
  const map = useMap();
  const cbRef = useRef(onZoomChange);
  useEffect(() => { cbRef.current = onZoomChange; }, [onZoomChange]);
  useEffect(() => {
    const handler = () => { if (cbRef.current) cbRef.current(map.getZoom()); };
    map.on('zoomend', handler);
    return () => map.off('zoomend', handler);
  }, [map]);
  return null;
};

// Imperatively restores the map view to the parent-supplied center + zoom
// when `nonce` increments. MapContainer only reads center/zoom at mount,
// so settings restored asynchronously need this nudge to actually move
// the map without tearing down the component.
const ViewRestorer = ({ mapCenter, zoom, nonce }) => {
  const map = useMap();
  useEffect(() => {
    if (nonce <= 0) return;
    if (!Array.isArray(mapCenter) || mapCenter.length !== 2) return;
    map.setView(mapCenter, typeof zoom === 'number' ? zoom : map.getZoom(), { animate: false });
    // Intentionally omit mapCenter/zoom from deps: we only want to restore on
    // an explicit nonce bump (e.g. after loadSettings), not every pan/zoom.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce]);
  return null;
};

const ImperativeZoom = ({ rectangleBounds, zoomToRectangleNonce, centerOnCoords, centerNonce }) => {
  const map = useMap();

  useEffect(() => {
    if (zoomToRectangleNonce > 0 && rectangleBounds) {
      map.fitBounds(L.latLngBounds(rectangleBounds), { padding: [50, 50] });
    }
  }, [zoomToRectangleNonce]);

  useEffect(() => {
    if (centerNonce > 0 && centerOnCoords && centerOnCoords.length >= 2) {
      map.fitBounds(L.latLngBounds(centerOnCoords), { padding: [60, 60] });
    }
  }, [centerNonce]);

  return null;
};

// Imperatively freezes leaflet's interaction handlers when `locked` is true.
// pointer-events:none alone wasn't enough on mobile (touch events still
// reached the map), so this disables drag / touch-zoom / etc. directly.
// scrollWheelZoom is intentionally left alone — MapContainer turns it off
// at mount and we don't want to re-enable it on unlock.
const MapLockHandler = ({ locked }) => {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const handlers = [
      map.dragging,
      map.touchZoom,
      map.doubleClickZoom,
      map.boxZoom,
      map.keyboard,
      map.tap, // undefined on non-touch builds, hence the filter below
    ].filter(Boolean);
    handlers.forEach((h) => (locked ? h.disable() : h.enable()));
  }, [map, locked]);
  return null;
};

const isValidCoordinate = (coord) => {
  return Array.isArray(coord) &&
    coord.length === 2 &&
    !isNaN(coord[0]) &&
    !isNaN(coord[1]) &&
    coord[0] >= -90 &&
    coord[0] <= 90 &&
    coord[1] >= -180 &&
    coord[1] <= 180;
};

// Observes the outer map container's size and forces leaflet to recompute
// its internal dimensions when it changes (otherwise the new area renders
// gray until the user pans). After invalidating, we re-fit the viewport to
// the print-area rectangle with a small margin — this keeps the rectangle
// visually anchored AND lets the map use the new available space (zoom in
// when the panel collapses, zoom out when it expands).
const AutoInvalidateSize = ({ rectangleBounds }) => {
  const map = useMap();
  const boundsRef = useRef(rectangleBounds);
  useEffect(() => { boundsRef.current = rectangleBounds; }, [rectangleBounds]);

  useEffect(() => {
    const container = map.getContainer();
    if (!container || typeof ResizeObserver === 'undefined') return;
    let firstObservation = true;
    const observer = new ResizeObserver(() => {
      map.invalidateSize({ pan: false, debounceMoveend: true });
      // Skip the very first ResizeObserver fire (initial mount); the map's
      // own initial setView already framed the rectangle correctly, and
      // calling fitBounds here would override the user's saved zoom.
      if (firstObservation) {
        firstObservation = false;
        return;
      }
      const b = boundsRef.current;
      if (
        Array.isArray(b) && b.length === 2 &&
        Array.isArray(b[0]) && Array.isArray(b[1]) &&
        b.flat().every((n) => typeof n === 'number' && !Number.isNaN(n))
      ) {
        map.fitBounds(L.latLngBounds(b), { padding: [50, 50] });
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [map]);
  return null;
};

const ActivityFetchHandler = ({ onFetchActivities }) => {
  const map = useMap();
  const timeoutRef = useRef(null);

  useEffect(() => {
    const reportBounds = () => {
      const b = map.getBounds();
      onFetchActivities({
        minLat: b.getSouth(),
        maxLat: b.getNorth(),
        minLon: b.getWest(),
        maxLon: b.getEast(),
      });
    };
    const handleMoveEnd = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(reportBounds, 500);
    };

    reportBounds();
    map.on('moveend', handleMoveEnd);
    map.on('zoomend', handleMoveEnd);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      map.off('moveend', handleMoveEnd);
      map.off('zoomend', handleMoveEnd);
    };
  }, [map, onFetchActivities]);

  return null;
};

// Re-centers the map when the user searches for a new place name. Uses the
// OpenStreetMap Nominatim endpoint; we only read lat/lon, no polygon/border
// overlay is drawn on the map anymore.
const PlaceCenterer = ({ placeName, shouldCenter, setShouldCenter }) => {
  const map = useMap();
  useEffect(() => {
    if (!shouldCenter || !placeName || placeName.length < 2) return;

    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(placeName)}&format=json`
        );
        const results = await response.json();
        if (cancelled || !results?.length) return;
        const lat = parseFloat(results[0].lat);
        const lon = parseFloat(results[0].lon);
        map.setView([lat, lon], map.getZoom());
        setShouldCenter(false);
      } catch (_err) {
        // Non-fatal: ignore lookup failures; user can still pan manually.
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [placeName, shouldCenter, setShouldCenter, map]);

  return null;
};

// Tile providers chosen to be commercial-use friendly with attribution.
// CARTO Voyager + Esri World Topo + Esri World Imagery all explicitly
// allow free commercial use as long as the bottom-right attribution
// line is shown — Leaflet renders that automatically from the
// `attribution` prop on TileLayer. `subdomains` is set per-style
// because CARTO uses 'abcd' (not the leaflet default 'abc') and the
// Esri services don't use {s} at all.
const TILE_STYLES = {
  standard: {
    // CARTO Voyager — colourful but uncluttered, ODbL data + CC-BY tiles.
    url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    subdomains: 'abcd',
    maxZoom: 20,
  },
  topo: {
    // Esri World Topo Map — Esri's free public service, commercial use
    // allowed with attribution under the ArcGIS Online terms.
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
    attribution:
      '&copy; <a href="https://www.esri.com/">Esri</a> &mdash; Source: Esri, USGS, NOAA, OpenStreetMap contributors',
    maxZoom: 19,
  },
  // Satellite = Esri imagery base + a transparent labels-only overlay
  // (no admin boundaries — those were on the previous overlay and the
  // user didn't want them). Labels come from CARTO's Positron labels
  // layer, which uses a cleaner sans-serif than the Voyager labels we
  // tried first. Dark text + white halo reads against both bright and
  // dark satellite imagery.
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution:
      '&copy; <a href="https://www.esri.com/">Esri</a> &mdash; Source: Esri, Maxar, Earthstar Geographics &copy; <a href="https://carto.com/attributions">CARTO</a>',
    maxZoom: 19,
    overlay: {
      url: "https://{s}.basemaps.cartocdn.com/rastertiles/light_only_labels/{z}/{x}/{y}{r}.png",
      subdomains: 'abcd',
      maxZoom: 20,
    },
  },
};

const MapComponent = ({
  mapCenter,
  zoom,
  rectangleBounds,
  setRectangleBounds,
  km2,
  selectedFormat,
  placeName,
  onMapCenterChange,
  shouldCenter,
  setShouldCenter,
  shouldZoom,
  onZoomChange,
  restoreViewNonce = 0,
  activitySettings = {},
  showActivityPolylines = true,
  activitySources = ['strava', 'garmin', 'manual'],
  hiddenActivityIds = [],
  activityPolylines = [],
  isLoadingActivities = false,
  onMapBoundsChange,
  activityDateFrom,
  activityDateTo,
  activityAllDates = true,
  includeCommutes = false,
  mapStyle = 'standard',
  onMapStyleChange,
  compact = false,
  onKm2Change,
  onFormatSelect,
  isPortrait = false,
  onTogglePortrait,
  onPlaceNameChange,
  searchResults = [],
  onSelectResult,
  recentPlaces = [],
  onSelectRecent,
  onClearPlace,
  onCenterPlace,
  onActivityLineWidthChange,
  onActivityLineWidthAutoChange,
  onActivityAlphaChange,
  onActivityModeChange,
  // Click target for the finder popup's "Date filter:" row. Lets the user
  // jump straight to the panel where the date pickers live.
  onOpenActivityPanel,
  // Imperative "fit the leaflet view to this lat/lon rectangle" trigger.
  // Used by the place-search Set button to auto-zoom to the place's
  // Nominatim boundingbox. Pass [[minLat, minLon], [maxLat, maxLon]]
  // as fitToBounds and bump fitToBoundsNonce to fire.
  fitToBounds = null,
  fitToBoundsNonce = 0,
  // Transient yellow polygon drawn over `placeHighlightGeoJSON` for ~5s
  // after `placeHighlightNonce` bumps. Mirrors the post-selection highlight
  // pattern used for activities. GeoJSON (not Rectangle) so country/island
  // shapes like NZ render correctly via MultiPolygon.
  placeHighlightGeoJSON = null,
  placeHighlightNonce = 0,
  // Tipps "Show me" target. Shape: { name: 'width' | 'location' | 'rectangle'
  // | 'km2' | 'format' | 'finder' | null, nonce: number }. Each bump of the
  // nonce flashes the matching control's border for ~2s.
  mapFlashTarget = { name: null, nonce: 0 },
}) => {
  const validCenter = isValidCoordinate(mapCenter) ? mapCenter : [0, 0];

  // Initial auto-fit was removed: the map opens on whatever `mapCenter`/`zoom`
  // it receives, and is only re-fit to the rectangle when the user clicks the
  // rectangle button (ImperativeZoom via nonce). Keeping MapController for
  // back-compat with the `shouldZoom` prop flow, but initial state is false.
  const [shouldZoomToRectangle, setShouldZoomToRectangle] = useState(false);

  // Mobile-only: lets the user freeze the map so a touch on it scrolls the
  // page instead of dragging the rectangle / panning the view. Toggled via
  // the lock chip rendered above the leaflet container; when on we set
  // pointer-events:none on the leaflet root so all touch input passes
  // through to the page, and we drop a centered lock icon overlay so the
  // frozen state is obvious.
  const [mapLocked, setMapLocked] = useState(false);


  // Nonces drive the imperative zoom effects inside MapContainer. We bump
  // them on button clicks instead of using a boolean flag so repeated clicks
  // with identical state still trigger a re-zoom.
  const [zoomToRectNonce, setZoomToRectNonce] = useState(0);
  const [centerNonce, setCenterNonce] = useState(0);
  const [centerCoords, setCenterCoords] = useState(null);

  const [finderOpen, setFinderOpen] = useState(false);
  const [finderQuery, setFinderQuery] = useState('');
  const [finderTypeFilter, setFinderTypeFilter] = useState(null);
  // Persist the highlighted activity across reloads so a user mid-inspection
  // doesn't lose their place. Restoring an id that's no longer in the bounds
  // is harmless — the renderers below find-by-id and silently no-op when it
  // returns undefined.
  const [highlightedActivityId, setHighlightedActivityId] = useState(() => {
    try {
      const raw = localStorage.getItem('highlightedActivityId');
      if (raw == null || raw === 'null') return null;
      const parsed = JSON.parse(raw);
      return parsed === '' ? null : parsed;
    } catch (_e) {
      return null;
    }
  });
  useEffect(() => {
    try {
      if (highlightedActivityId == null) {
        localStorage.removeItem('highlightedActivityId');
      } else {
        localStorage.setItem('highlightedActivityId', JSON.stringify(highlightedActivityId));
      }
    } catch (_e) { /* quota / private mode — no-op */ }
  }, [highlightedActivityId]);

  // The thick red highlight is only meant as a transient "yes, that's the
  // one" visual — keeping it on permanently competes with the rest of the
  // map. After 5s we drop the emphasis but leave the activity selected so
  // the corner buttons still work.
  const [highlightVisible, setHighlightVisible] = useState(false);
  const highlightTimerRef = useRef(null);
  useEffect(() => {
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    if (highlightedActivityId && highlightVisible) {
      highlightTimerRef.current = setTimeout(() => {
        setHighlightVisible(false);
        highlightTimerRef.current = null;
      }, 5000);
    }
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, [highlightedActivityId, highlightVisible]);

  // Mirrors the activity-highlight pattern (5s flash) for the place
  // boundary after a Set, so the user sees what shape the auto-fit
  // was based on. Only the visibility flag has a timer — the geometry
  // itself stays set until a new Set, but gets hidden after 5s.
  const [placeHighlightVisible, setPlaceHighlightVisible] = useState(false);
  const placeHighlightTimerRef = useRef(null);
  useEffect(() => {
    if (!placeHighlightNonce || !placeHighlightGeoJSON) return;
    setPlaceHighlightVisible(true);
    if (placeHighlightTimerRef.current) clearTimeout(placeHighlightTimerRef.current);
    placeHighlightTimerRef.current = setTimeout(() => {
      setPlaceHighlightVisible(false);
      placeHighlightTimerRef.current = null;
    }, 5000);
    return () => {
      if (placeHighlightTimerRef.current) clearTimeout(placeHighlightTimerRef.current);
    };
  }, [placeHighlightNonce]);

  // Marks the location pill's center-arrow as "ready to fire" after the user
  // picks a suggestion or a recent place. Reset once they click it (or clear
  // the place). Purely cosmetic — draws attention to the next step.
  const [pendingCenter, setPendingCenter] = useState(false);
  useEffect(() => {
    if (!placeName) setPendingCenter(false);
  }, [placeName]);

  // In-map popups for the size (km²), format, and location controls, so the
  // user doesn't have to leave the map view to adjust them.
  const [sizePopupOpen, setSizePopupOpen] = useState(false);
  const [formatPopupOpen, setFormatPopupOpen] = useState(false);
  const [locationPopupOpen, setLocationPopupOpen] = useState(false);
  const [lineWidthPopupOpen, setLineWidthPopupOpen] = useState(false);
  // Drives a 2s border-flash on whichever map control the Tipps "Show me"
  // is pointing at. One state instead of N nonces. The ref guards against
  // firing on initial mount with an already-non-zero nonce — this component
  // is unmounted while the user is on the preview section, so a simple
  // value comparison would mis-fire after the first Show Me.
  const [flashingControl, setFlashingControl] = useState(null);
  const prevFlashNonceRef = useRef(mapFlashTarget?.nonce || 0);
  useEffect(() => {
    const nonce = mapFlashTarget?.nonce || 0;
    if (nonce === prevFlashNonceRef.current) return;
    prevFlashNonceRef.current = nonce;
    setFlashingControl(mapFlashTarget?.name || null);
    const t = setTimeout(() => setFlashingControl(null), 2000);
    return () => clearTimeout(t);
  }, [mapFlashTarget?.nonce, mapFlashTarget?.name]);
  const flashClass = (name) => (flashingControl === name ? 'animate-tip-flash' : '');

  // Mutual-exclusion helper: opening any in-map popup closes all the others.
  // Pass the name of the popup being toggled so its own setter is left alone
  // (the caller toggles it on the same click).
  const closeOtherPopups = (except) => {
    if (except !== 'finder') setFinderOpen(false);
    if (except !== 'size') setSizePopupOpen(false);
    if (except !== 'format') setFormatPopupOpen(false);
    if (except !== 'location') setLocationPopupOpen(false);
    if (except !== 'lineWidth') setLineWidthPopupOpen(false);
  };

  // Local-only state for the search input inside the location popup. We
  // deliberately don't seed it from `placeName` — once the user has
  // centered on a place (or reloaded the page), the input should be a
  // blank canvas for the next search rather than preserving the previous
  // query, which mismatches reality after a manual map drag.
  const [searchInput, setSearchInput] = useState('');

  // Visible (= unhidden) activity count drives both the auto width
  // suggestion and the popup info row. Hidden ids come from the activity
  // filter panel (per-type/per-id toggles), so toggling there feeds back
  // into the auto width.
  const visibleActivityCount = useMemo(() => {
    if (!hiddenActivityIds || hiddenActivityIds.length === 0) return activityPolylines.length;
    const hidden = new Set(hiddenActivityIds);
    let n = 0;
    for (const a of activityPolylines) if (!hidden.has(a.id)) n += 1;
    return n;
  }, [activityPolylines, hiddenActivityIds]);

  // Auto-mode line width is now derived in dashboard.js from activitiesInView
  // so the recalc runs in every section. Keep the boolean here for the chip
  // label and the popup hint copy.
  const lineWidthAuto = activitySettings?.lineWidthAuto === true;

  // Outside-click dismiss. Each ref wraps its trigger (and sometimes its
  // popup); a mousedown outside all of them closes whichever panel is open.
  const finderRef = useRef(null);
  const sizeRef = useRef(null);
  const formatRef = useRef(null);
  const locationRef = useRef(null);
  const lineWidthRef = useRef(null);

  useEffect(() => {
    if (!finderOpen && !sizePopupOpen && !formatPopupOpen && !locationPopupOpen && !lineWidthPopupOpen) return;
    const handleDown = (e) => {
      const insideAny = [finderRef, sizeRef, formatRef, locationRef, lineWidthRef].some(
        (r) => r.current && r.current.contains(e.target)
      );
      if (insideAny) return;
      setFinderOpen(false);
      setSizePopupOpen(false);
      setFormatPopupOpen(false);
      setLocationPopupOpen(false);
      setLineWidthPopupOpen(false);
    };
    document.addEventListener('mousedown', handleDown);
    return () => document.removeEventListener('mousedown', handleDown);
  }, [finderOpen, sizePopupOpen, formatPopupOpen, locationPopupOpen, lineWidthPopupOpen]);

  useEffect(() => {
    setShouldZoomToRectangle(shouldZoom);
  }, [shouldZoom]);

  const handleZoomToRectangle = () => {
    setZoomToRectNonce((n) => n + 1);
  };

  const handleSelectActivity = (activity) => {
    setHighlightedActivityId(activity.id);
    setCenterCoords(activity.coordinates);
    setCenterNonce((n) => n + 1);
    setFinderOpen(false);
    setHighlightVisible(true);
    handleFitToActivity(activity);
  };

  // Resize the print-area rectangle to fit a given activity's bounding box
  // (with a small margin) and flip the portrait flag to match its dominant
  // axis. When called without an explicit activity, falls back to the
  // currently highlighted one. No-op if the parent doesn't pass `onKm2Change`.
  const handleFitToActivity = (explicitActivity) => {
    const activity = explicitActivity
      || activityPolylines.find((a) => a.id === highlightedActivityId);
    if (!activity || !Array.isArray(activity.coordinates) || activity.coordinates.length < 2) return;
    if (!onKm2Change) return;

    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    for (const [lat, lng] of activity.coordinates) {
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
    }

    // Approximate meters per degree — good enough at print-area scales where
    // the activity spans a few km. Avoids pulling in turf just for this.
    const avgLat = (minLat + maxLat) / 2;
    const metersPerDegLat = 111320;
    const metersPerDegLng = 111320 * Math.cos((avgLat * Math.PI) / 180);
    const activityWidthM = (maxLng - minLng) * metersPerDegLng;
    const activityHeightM = (maxLat - minLat) * metersPerDegLat;

    // Orientation: match the print-area's long axis to the activity's.
    const activityIsPortrait = activityHeightM > activityWidthM;
    const willBePortrait = onTogglePortrait ? activityIsPortrait : isPortrait;
    if (onTogglePortrait && isPortrait !== activityIsPortrait) onTogglePortrait();

    // Compute the smallest km² such that the rectangle (in the NEW
    // orientation) covers the activity bbox with a 10% margin. The
    // rectangle is width = sqrt(area * R), height = sqrt(area / R) where
    // R = aspectRatio in the new orientation.
    const baseAR = selectedFormat?.aspectRatio || 4 / 3;
    // selectedFormat.aspectRatio already reflects the current orientation;
    // if we're flipping, invert it for the calculation.
    const R = willBePortrait === isPortrait ? baseAR : 1 / baseAR;
    const margin = 1.1;
    const targetW = activityWidthM * margin;
    const targetH = activityHeightM * margin;
    const areaByWidth = (targetW * targetW) / R;
    const areaByHeight = targetH * targetH * R;
    const areaM2 = Math.max(areaByWidth, areaByHeight);
    // Auto-fit km² for the activity: no hard upper bound — large activities
    // (multi-day tours, long flights) can legitimately span huge areas, and
    // Basic style mode handles those above 50k km².
    const km2 = Math.max(1, Math.ceil(areaM2 / 1e6));

    if (onKm2Change) onKm2Change(km2);
    // Map center is already on (or near) the activity from the Activity
    // finder click that ran before this; EditableRectangle will recompute
    // bounds around the current center once km²/aspectRatio change.
  };

  // Unique activity types currently visible on the map, sorted by count
  // (most common first). Drives the category chips in the finder popup.
  const finderCategories = useMemo(() => {
    const counts = new Map();
    for (const a of activityPolylines) {
      if (hiddenActivityIds.includes(a.id)) continue;
      const t = a.type || 'Other';
      counts.set(t, (counts.get(t) || 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => ({ type, count }));
  }, [activityPolylines, hiddenActivityIds]);

  const filteredFinderActivities = useMemo(() => {
    const q = finderQuery.trim().toLowerCase();
    let pool = activityPolylines.filter((a) => !hiddenActivityIds.includes(a.id));
    // A chosen category hides everything else, per the user's spec: clicking
    // a category button collapses the list to just that type.
    if (finderTypeFilter) {
      pool = pool.filter((a) => (a.type || 'Other') === finderTypeFilter);
    } else if (q) {
      pool = pool.filter(
        (a) =>
          (a.name || '').toLowerCase().includes(q) ||
          (a.type || '').toLowerCase().includes(q)
      );
    } else {
      // With no category picked and no query, show nothing until the user
      // narrows the list — avoids dumping hundreds of rows into the popup.
      return [];
    }
    // Newest first when a date is present.
    return pool
      .slice()
      .sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''))
      .slice(0, 200);
  }, [finderQuery, finderTypeFilter, activityPolylines, hiddenActivityIds]);

  // Newest visible activity (by startDate). Surfaced as its own row in the
  // finder so the user can re-center on their most recent activity in one
  // click without searching by name or filtering by type.
  const latestActivity = useMemo(() => {
    let best = null;
    for (const a of activityPolylines) {
      if (hiddenActivityIds.includes(a.id)) continue;
      if (!a.startDate) continue;
      if (!best || a.startDate > best.startDate) best = a;
    }
    return best;
  }, [activityPolylines, hiddenActivityIds]);

  const formatActivityDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // One-line summary of the current activity date filter, surfaced at the
  // top of the finder popup so the user can see at a glance which slice of
  // the data they're searching across (mirrors the date controls in the
  // Strava/Garmin panel).
  const finderDateSummary = (() => {
    if (activityAllDates) return 'All dates';
    const from = formatActivityDate(activityDateFrom);
    const to = formatActivityDate(activityDateTo);
    if (from && to) return `${from} – ${to}`;
    if (from) return `from ${from}`;
    if (to) return `until ${to}`;
    return 'All dates';
  })();

  // Extracted because the finder popup is now rendered anchored to its
  // trigger button inside the right-side column, not as a top-level sibling.
  const renderFinderContent = () => (
    <>
      {onOpenActivityPanel ? (
        <button
          type="button"
          onClick={() => {
            setFinderOpen(false);
            onOpenActivityPanel();
          }}
          title="Edit date filter in the activity panel"
          className="w-full px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1.5 hover:bg-gray-50 cursor-pointer"
        >
          <span>Date filter:</span>
          <span className="text-gray-700 normal-case tracking-normal underline decoration-dotted underline-offset-2">
            {finderDateSummary}
          </span>
        </button>
      ) : (
        <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
          <span>Date filter:</span>
          <span className="text-gray-700 normal-case tracking-normal">
            {finderDateSummary}
          </span>
        </div>
      )}
      {finderTypeFilter ? (
        <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <span className="text-sm font-medium text-gray-800">{finderTypeFilter}</span>
          <button
            type="button"
            onClick={() => setFinderTypeFilter(null)}
            className="text-xs text-gray-500 hover:text-gray-900 p-0"
          >
            ← categories
          </button>
        </div>
      ) : (
        <>
          <div className="px-3 pt-3 pb-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">
              Filter by type
            </div>
            {finderCategories.length === 0 ? (
              <div className="text-sm text-gray-500">No activities in view.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {finderCategories.map(({ type, count }) => {
                  const color = getActivityTypeColor(type, activitySettings);
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        setFinderTypeFilter(type);
                        setFinderQuery('');
                      }}
                      className="text-xs px-2.5 py-1 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-800 border border-transparent flex items-center gap-1.5"
                    >
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: color }}
                      />
                      {type}
                      <span className="text-gray-500">({count})</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 px-3 py-1">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-[10px] uppercase tracking-wider text-gray-400">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Latest visible activity — one-click recenter on the most recent
              one in view. Hidden when there are no dated activities to pick
              from (so we don't render a useless empty section). */}
          {latestActivity && (
            <div className="px-3 pb-2">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">
                Latest activity
              </div>
              <button
                type="button"
                onClick={() => handleSelectActivity(latestActivity)}
                className={`w-full text-left px-2.5 py-1.5 text-sm border border-gray-200 rounded hover:bg-gray-50 flex items-start gap-2 ${highlightedActivityId === latestActivity.id ? 'bg-yellow-50' : 'bg-white'}`}
              >
                <span
                  className="inline-block w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                  style={{ backgroundColor: getActivityTypeColor(latestActivity.type, activitySettings) }}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate text-gray-900">{latestActivity.name || '(unnamed activity)'}</div>
                  <div className="text-xs text-gray-500">
                    {formatActivityDate(latestActivity.startDate) || (latestActivity.type || 'Other')}
                  </div>
                </div>
              </button>
            </div>
          )}

          <div className="flex items-center gap-2 px-3 py-1">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-[10px] uppercase tracking-wider text-gray-400">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <div className="px-3 pb-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={finderQuery}
                onChange={(e) => setFinderQuery(e.target.value)}
                placeholder="Search by name…"
                autoFocus
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-gray-400"
              />
            </div>
          </div>
        </>
      )}

      <div className="overflow-y-auto flex-1 border-t border-gray-100">
        {!finderTypeFilter && !finderQuery.trim() ? (
          <div className="p-3 text-xs text-gray-400 text-center">
            Pick a category or start typing.
          </div>
        ) : filteredFinderActivities.length === 0 ? (
          <div className="p-3 text-sm text-gray-500">No activities match.</div>
        ) : (
          filteredFinderActivities.map((a) => {
            const color = getActivityTypeColor(a.type, activitySettings);
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => handleSelectActivity(a)}
                className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 hover:bg-gray-50 flex items-start gap-2 ${highlightedActivityId === a.id ? 'bg-yellow-50' : 'bg-white'}`}
              >
                <span
                  className="inline-block w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                  style={{ backgroundColor: color }}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate text-gray-900">{a.name || '(unnamed activity)'}</div>
                  <div className="text-xs text-gray-500">
                    {formatActivityDate(a.startDate) || (a.type || 'Other')}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </>
  );

  const activeStyle = TILE_STYLES[mapStyle] ? mapStyle : 'standard';
  const {
    url: tileUrl,
    attribution: tileAttribution,
    subdomains: tileSubdomains,
    maxZoom: tileMaxZoom,
    overlay: tileOverlay,
  } = TILE_STYLES[activeStyle];

  const handleCenterChange = (newCenter) => {
    if (onMapCenterChange) onMapCenterChange(newCenter);
  };

  const resolveWeight = (lineWidth) => {
    if (typeof lineWidth === 'number') return lineWidth;
    if (lineWidth === 'very thin') return 0.5;
    if (lineWidth === 'thin') return 0.75;
    if (lineWidth === 'thick') return 3;
    if (lineWidth === 'very thick') return 5;
    // Legacy "normal" / unknown strings fall through to the same default
    // first-time users get, so the preview matches the chip on both
    // desktop and mobile (no viewport-based scaling — the user-picked
    // numeric value is the source of truth on every device).
    return 0.8;
  };

  const hiddenIdSet = useMemo(
    () => new Set(hiddenActivityIds),
    [hiddenActivityIds],
  );

  // Visible polylines for the heatmap renderer. Stable reference when
  // nothing in the input actually changes, so the HeatLayer's redraw
  // effect doesn't re-run on every parent re-render. We pass raw
  // polylines (no densification) — the canvas-based HeatLayer draws
  // continuous strokes natively.
  const heatmapPolylines = useMemo(() => {
    if (activitySettings?.mode !== 'heatmap') return null;
    return activityPolylines.filter((a) => !hiddenIdSet.has(a.id));
  }, [activityPolylines, hiddenIdSet, activitySettings?.mode]);

  // Mobile gets a taller viewport-based height so the map dominates the
  // screen; desktop keeps its aspect ratio to preserve the print-preview
  // framing alongside the sidebar.
  const desktopAspect = compact ? 'lg:aspect-[4/3]' : 'lg:aspect-[3/2]';
  // Mobile: cap the leaflet container so it never pushes the page past
  // the viewport. 55 dvh (dynamic viewport height) accounts for the
  // browser's address bar on iOS / Android — unlike `vh`, it shrinks
  // when the bar slides in, so we don't end up with a phantom scroll.
  // Falls back to vh on browsers without dvh support.
  // const mobileHeight = 'h-[55vh] h-[55dvh] max-h-[55dvh] lg:h-auto lg:max-h-none';
  const mobileHeight = 'h-[65vh] h-[65dvh] max-h-[65dvh] lg:h-auto max-h-none';

  return (
    <div className="space-y-4">
      <div className={`relative w-full ${mobileHeight} ${desktopAspect} border-4 lg:border-8 border-black rounded`}>
        <MapContainer
          center={validCenter}
          zoom={zoom}
          // pointer-events:none when locked — touches fall through to the
          // page so the user can scroll past the full-screen mobile map
          // without grabbing the rectangle. Re-enabled on unlock.
          style={{ height: '100%', width: '100%', pointerEvents: mapLocked ? 'none' : 'auto' }}
          zoomControl={true}
          scrollWheelZoom={false}
        >
          <TileLayer
            // key forces a clean remount when the style changes — without
            // it leaflet keeps the previous layer's tile cache + attribution
            // around briefly when switching providers.
            key={activeStyle}
            url={tileUrl}
            attribution={tileAttribution}
            subdomains={tileSubdomains || 'abc'}
            maxZoom={tileMaxZoom || 19}
          />
          {/* Transparent labels/boundaries overlay for the 'hybrid' style
              (and any future overlay-bearing style). Rendered after the
              base so it draws on top; pane is the default overlayPane
              so its z-index sits above tiles but below activity polylines. */}
          {tileOverlay && (
            <TileLayer
              key={`${activeStyle}-overlay`}
              url={tileOverlay.url}
              attribution={tileOverlay.attribution || ''}
              subdomains={tileOverlay.subdomains || 'abc'}
              maxZoom={tileOverlay.maxZoom || tileMaxZoom || 19}
              pane="overlayPane"
            />
          )}

          {/* Heatmap mode. Polylines are passed undensified — the
              canvas-based HeatLayer renders continuous 1 px strokes,
              so lines stay sharp at every zoom level and pan is
              instant (redraw on moveend/zoomend only). */}
          {showActivityPolylines && activitySettings?.mode === 'heatmap'
            && heatmapPolylines && heatmapPolylines.length > 0 && (() => {
              const heatmapOpacity = typeof activitySettings?.alpha === 'number'
                ? activitySettings.alpha
                : 0.85;
              return (
                <HeatLayer
                  polylines={heatmapPolylines}
                  baseColor={activitySettings?.color || '#ff6a00'}
                  opacity={heatmapOpacity}
                />
              );
            })()}

          {showActivityPolylines && activitySettings?.mode !== 'heatmap' && activityPolylines
            .filter(activity => !hiddenIdSet.has(activity.id))
            .map(activity => (
              <Polyline
                key={activity.id}
                positions={activity.coordinates}
                pathOptions={{
                  // Match the per-type checkbox tick color in ActivityFilterPanel
                  // so the map is visually tied to the filter list. Line width
                  // and opacity stay on the common settings — per-type values
                  // only take effect in the generated image.
                  color: getActivityTypeColor(activity.type, activitySettings),
                  weight: resolveWeight(activitySettings?.lineWidth),
                  opacity: activitySettings?.alpha != null ? activitySettings.alpha : 0.8,
                }}
              />
            ))}

          {/* Highlighted activity drawn last so it sits on top of the regular
              polylines; distinct color + thicker weight makes it pop. Only
              shown for ~5s after selection — see the highlightVisible timer. */}
          {highlightedActivityId && highlightVisible && (() => {
            const a = activityPolylines.find((x) => x.id === highlightedActivityId);
            if (!a || a.coordinates.length < 2) return null;
            return (
              <Polyline
                positions={a.coordinates}
                pathOptions={{ color: HIGHLIGHT_COLOR, weight: 5, opacity: 1 }}
              />
            );
          })()}

          {/* Yellow place-boundary flash, ~5s after a place Set. Sits below
              the print-area frame; no fill so the basemap stays visible.
              `key` forces a fresh GeoJSON layer per Set — the component
              memoises its data prop, so without a key changes wouldn't
              re-render the polygon. */}
          {placeHighlightVisible && placeHighlightGeoJSON && (
            <GeoJSON
              key={placeHighlightNonce}
              data={placeHighlightGeoJSON}
              style={{ color: '#fbbf24', weight: 4, fillOpacity: 0 }}
              interactive={false}
            />
          )}

          <EditableRectangle
            bounds={rectangleBounds}
            setBounds={setRectangleBounds}
            km2={km2}
            aspectRatio={selectedFormat.aspectRatio}
            onCenterChange={handleCenterChange}
          />

          <MapController
            rectangleBounds={rectangleBounds}
            shouldZoomToRectangle={shouldZoomToRectangle}
            setShouldZoomToRectangle={setShouldZoomToRectangle}
          />
          <ImperativeZoom
            rectangleBounds={rectangleBounds}
            zoomToRectangleNonce={zoomToRectNonce}
            centerOnCoords={centerCoords}
            centerNonce={centerNonce}
          />
          {/* External fit-to-bounds trigger: lets the parent pass arbitrary
              lat/lon rectangles (e.g. a place's Nominatim boundingbox) and
              fitBounds the leaflet view to them when the nonce bumps. */}
          <ImperativeZoom
            rectangleBounds={null}
            zoomToRectangleNonce={0}
            centerOnCoords={fitToBounds}
            centerNonce={fitToBoundsNonce}
          />
          <ViewRestorer
            mapCenter={mapCenter}
            zoom={zoom}
            nonce={restoreViewNonce}
          />
          <ZoomListener onZoomChange={onZoomChange} />
          <PlaceCenterer
            placeName={placeName}
            shouldCenter={shouldCenter}
            setShouldCenter={setShouldCenter}
          />
          {showActivityPolylines && onMapBoundsChange && (
            <ActivityFetchHandler onFetchActivities={onMapBoundsChange} />
          )}
          <AutoInvalidateSize rectangleBounds={rectangleBounds} />
          <MapLockHandler locked={mapLocked} />
        </MapContainer>

        {/* Mobile lock toggle. Sits bottom-right just above the Leaflet
            attribution strip (~16px tall) so it doesn't collide with the
            km²/format/finder stack on the right or the +/- zoom controls
            on the left. Hidden on desktop where the map shares the row
            with a sidebar and accidental drags aren't an issue. */}
        {/* Mirrors the "Plot activities" tab on the opposite edge — same
            shape (rounded on the inward side, hugging the map border) but
            with the lock affordance and red/green coloring keyed off
            mapLocked. Mobile only; desktop has the sidebar so the full-
            screen drag-trap doesn't apply. */}
        {/* Mirrors the "Plot activities" tab on the opposite edge — same
            shape and overall height, but anchored flush to the very left
            (no gap to the map border) with the lock affordance and a
            red/green coloring keyed off mapLocked. Mobile only. */}
        <button
          type="button"
          onClick={() => setMapLocked((v) => !v)}
          title={mapLocked ? 'Unlock map' : 'Lock map'}
          className={`lg:hidden flex absolute left-0 top-1/2 -translate-y-1/2 z-[900] items-center gap-1 text-white border rounded-r-xl shadow-lg pr-2 pl-1 py-2
            ${mapLocked
              ? 'bg-red-600 hover:bg-red-700 border-red-700'
              : 'bg-green-600 hover:bg-green-700 border-green-700'} ${flashClass('lock')}`}
        >
          {mapLocked ? <Lock size={28} /> : <Unlock size={28} />}
          <span className="text-[10px] font-semibold whitespace-nowrap leading-tight">
            {mapLocked ? <>Unlock<br />map</> : <>Lock<br />map</>}
          </span>
        </button>

        {/* Centered lock badge — pointer-events:none so it never swallows
            a tap intended for the lock button or for the page beneath. */}
        {mapLocked && (
          <div className="absolute inset-0 z-[999] flex items-center justify-center pointer-events-none">
            <div className="bg-gray-900/30 text-white rounded-full p-6">
              <Lock size={48} strokeWidth={2.5} />
            </div>
          </div>
        )}

        {isLoadingActivities && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white p-2 rounded shadow-md text-sm flex items-center gap-2 z-[1000]">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            Loading activities...
          </div>
        )}

        {/* Top-right stack: km², format, and the activity finder — stacked
            vertically in that order. Each trigger opens a popup anchored
            below itself via `top-full right-0`. */}
        <div className="absolute top-2 right-2 lg:top-4 lg:right-4 z-[1000] flex flex-col gap-1 lg:gap-2 items-end">
          {onKm2Change && (
            <div className="relative" ref={sizeRef}>
              <button
                type="button"
                onClick={() => {
                  closeOtherPopups('size');
                  setSizePopupOpen((v) => !v);
                }}
                title="Change area size"
                className={`rounded shadow-md px-2.5 py-1 lg:px-4 lg:py-2.5 border border-gray-200 text-xs lg:text-sm font-medium flex items-center gap-0.5 lg:gap-1.5 ${sizePopupOpen ? 'bg-gray-900 text-white' : 'bg-white text-gray-800 hover:bg-gray-100'} ${flashClass('km2')}`}
              >
                <Ruler size={14} className="lg:hidden" />
                <Ruler size={17} className="hidden lg:block" />
                <span>{formatKm2(km2)}</span>
              </button>
              {sizePopupOpen && (
                <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 p-3 space-y-3 z-[1100]">
                  <div className="flex items-baseline gap-2 justify-center">
                    <input
                      type="number"
                      min={1}
                      value={km2}
                      onChange={(e) => {
                        const v = e.target.value === '' ? '' : Number(e.target.value);
                        if (v === '' || (Number.isFinite(v) && v > 0)) onKm2Change(v);
                      }}
                      className="w-28 text-center text-lg border-2 border-gray-200 focus:border-blue-500 rounded-xl py-1 outline-none"
                    />
                    <span className="text-xs text-gray-600">km²</span>
                  </div>
                  {/* Slider tops out at 100k km² for usable resolution; the
                      numeric input above accepts any value above that, so
                      country/continent renders are still reachable. */}
                  <input
                    type="range"
                    min={1}
                    max={100000}
                    step={1}
                    value={Math.min(Number(km2) || 1, 100000)}
                    onChange={(e) => onKm2Change(Number(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex flex-wrap gap-1.5">
                    {[10, 50, 100, 500, 1000, 5000, 10000, 50000, 500000].map((preset) => (
                      <Button
                        key={preset}
                        type="button"
                        onClick={() => onKm2Change(preset)}
                        className={`text-xs px-2.5 py-1 rounded-full border h-auto ${Number(km2) === preset
                          ? 'bg-gray-900 text-white border-gray-900'
                          : 'bg-white border-gray-300 hover:bg-gray-100 text-gray-800'}`}
                        variant="ghost"
                      >
                        {formatKm2(preset).replace(' km²', '')}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {onFormatSelect && (
            <div className="relative" ref={formatRef}>
              <button
                type="button"
                onClick={() => {
                  closeOtherPopups('format');
                  setFormatPopupOpen((v) => !v);
                }}
                title="Change format"
                className={`rounded shadow-md px-2.5 py-1 lg:px-4 lg:py-2.5 border border-gray-200 text-xs lg:text-sm font-medium flex items-center gap-0.5 lg:gap-1.5 ${formatPopupOpen ? 'bg-gray-900 text-white' : 'bg-white text-gray-800 hover:bg-gray-100'} ${flashClass('format')}`}
              >
                <LayoutGrid size={14} className="lg:hidden" />
                <LayoutGrid size={17} className="hidden lg:block" />
                <span>
                  {selectedFormat?.value === 'custom'
                    ? (selectedFormat.label || 'Custom').trim()
                    : (selectedFormat?.value || 'Format')}
                </span>
              </button>
              {formatPopupOpen && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-200 p-3 space-y-3 z-[1100]">
                  <div className="grid grid-cols-2 gap-2">
                    {Object.keys(FORMATS).map((format) => {
                      const [w, h] = FORMATS[format].S.split('x').map((d) =>
                        parseFloat(d.replace(',', '.'))
                      );
                      const display = isPortrait ? `${w}×${h}` : `${h}×${w}`;
                      const isActive = selectedFormat?.value === format;
                      return (
                        <Button
                          key={format}
                          type="button"
                          onClick={() => {
                            const dims = {
                              width: w,
                              height: h,
                              originalWidth: isPortrait ? h : w,
                              originalHeight: isPortrait ? w : h,
                            };
                            onFormatSelect({
                              value: format,
                              size: 'S',
                              label: ` ${format}`,
                              dimensions: dims,
                              aspectRatio: dims.width / dims.height,
                            });
                          }}
                          className={`p-2 border rounded-xl text-center transition-colors h-auto ${isActive
                            ? 'border-blue-500 bg-blue-50 text-gray-900'
                            : 'border-gray-200 hover:bg-gray-50 bg-white text-gray-900'}`}
                          variant="ghost"
                        >
                          <div className="font-medium text-sm">{format}</div>
                        </Button>
                      );
                    })}
                  </div>
                  {onTogglePortrait && (
                    <div className="flex items-center justify-center pt-2 border-t border-gray-100">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isPortrait}
                          onChange={onTogglePortrait}
                          className="h-4 w-4"
                        />
                        Portrait
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Activity finder trigger — only rendered when the activity
                overlay is enabled and at least one source is selected, since
                there's nothing to find otherwise. */}
          {showActivityPolylines && activitySources.length > 0 && (
            <div className="relative" ref={finderRef}>
              {/* Pill mirrors the location-search control: trigger on the
                  left, optional clear-X on the right when an activity is
                  selected. */}
              <div className={`flex items-stretch rounded shadow-md border border-gray-200 overflow-hidden ${finderOpen ? 'bg-gray-900' : 'bg-white'} ${flashClass('finder')}`}>
                <button
                  type="button"
                  onClick={() => {
                    closeOtherPopups('finder');
                    setFinderOpen((v) => !v);
                    setFinderTypeFilter(null);
                    setFinderQuery('');
                  }}
                  title="Center on activity"
                  className={`px-2.5 py-1 lg:px-4 lg:py-2.5 text-xs lg:text-sm font-medium flex items-center gap-0.5 lg:gap-1.5 ${finderOpen ? 'text-white hover:bg-gray-800' : 'text-gray-800 hover:bg-gray-100'}`}
                >
                  <Search size={14} className="lg:hidden" />
                  <Search size={17} className="hidden lg:block" />
                  <span>Activity</span>
                </button>
                {highlightedActivityId && (
                  <>
                    <div className={`w-px shrink-0 ${finderOpen ? 'bg-gray-700' : 'bg-gray-300'}`} />
                    <button
                      type="button"
                      onClick={() => {
                        setHighlightedActivityId(null);
                        setHighlightVisible(false);
                      }}
                      title="Clear selected activity"
                      aria-label="Clear selected activity"
                      className={`flex items-center px-1.5 lg:px-2 shrink-0 transition-colors ${finderOpen ? 'text-red-300 hover:bg-gray-800' : 'text-red-600 hover:bg-gray-100'}`}
                    >
                      <X size={14} className="lg:hidden" />
                      <X size={17} className="hidden lg:block" />
                    </button>
                  </>
                )}
              </div>
              {finderOpen && (
                <div className="absolute top-full right-0 mt-2 w-80 max-h-[28rem] bg-white rounded-xl shadow-xl border border-gray-200 flex flex-col overflow-hidden z-[1100]">
                  {renderFinderContent()}
                </div>
              )}
            </div>
          )}

          {/* Activity render mode toggle — segmented control with both
              options visible at once. Active side is filled black with
              white text; inactive side is a plain white button. Both
              halves remain hit targets so a single click flips. */}
          {showActivityPolylines && activitySources.length > 0 && onActivityModeChange && (() => {
            const isHeatmap = activitySettings?.mode === 'heatmap';
            const base = "px-2.5 py-1 lg:px-4 lg:py-2.5 text-xs lg:text-sm font-medium transition-colors flex items-center gap-0.5 lg:gap-1";
            return (
              <div className="rounded shadow-md border border-gray-200 bg-white overflow-hidden flex items-stretch">
                <button
                  type="button"
                  onClick={() => { if (isHeatmap) onActivityModeChange('polylines'); }}
                  title="Show individual activity lines"
                  className={`${base} ${!isHeatmap ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                >
                  <span>Lines</span>
                </button>
                <div className="w-px bg-gray-200" />
                <button
                  type="button"
                  onClick={() => { if (!isHeatmap) onActivityModeChange('heatmap'); }}
                  title="Show frequency heatmap"
                  className={`${base} ${isHeatmap ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                >
                  <span>Heatmap</span>
                </button>
              </div>
            );
          })()}

          {/* Activity line-width control. The user-facing scale is 1–100
              for ergonomics, but the underlying value the renderer reads is
              0.01–5 — keeping it linear so the same number drives Leaflet
              weight, the matplotlib render, and the saved settings dict.
              See get_line_width() in backend/location_map.py for how the
              backend interprets the stored value.

              Two modes: auto (width tracks visible activity count via the
              suggestion curve) and manual (user-set, value sticks). The
              button label reflects the mode; the popup contents toggle
              between an info row and the manual controls. */}
          {/* Heatmap mode hijacks the line-width chip: same physical
              control, but the slider drives the heatmap layer opacity
              (0..1) instead of line weight. The underlying value is
              activities.alpha so the customize-sheet opacity slider
              and this chip stay in sync. */}
          {showActivityPolylines && activitySources.length > 0
            && activitySettings?.mode === 'heatmap'
            && onActivityAlphaChange && (() => {
              const opacity = typeof activitySettings?.alpha === 'number'
                ? activitySettings.alpha
                : 0.85;
              const pct = Math.round(Math.max(0, Math.min(1, opacity)) * 100);
              const setPct = (p) => {
                const clamped = Math.max(0, Math.min(100, p));
                onActivityAlphaChange(Math.round(clamped) / 100);
              };
              return (
                <div className="relative" ref={lineWidthRef}>
                  <button
                    type="button"
                    onClick={() => {
                      closeOtherPopups('lineWidth');
                      setLineWidthPopupOpen((v) => !v);
                    }}
                    title="Adjust heatmap opacity"
                    className={`rounded shadow-md px-2.5 py-1 lg:px-4 lg:py-2.5 border border-gray-200 text-xs lg:text-sm font-medium flex items-center gap-0.5 lg:gap-1.5 ${lineWidthPopupOpen ? 'bg-gray-900 text-white' : 'bg-white text-gray-800 hover:bg-gray-100'} ${flashClass('width')}`}
                  >
                    <span>Opacity {pct}%</span>
                  </button>
                  {lineWidthPopupOpen && (
                    <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 p-3 space-y-2 z-[1100]">
                      <div className="text-xs font-medium text-gray-700">Heatmap opacity ({pct}%)</div>
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          min={0}
                          max={100}
                          step={1}
                          value={pct}
                          onChange={(e) => setPct(parseInt(e.target.value, 10))}
                          className="flex-1 min-w-0"
                        />
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          value={pct}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10);
                            if (!isNaN(n)) setPct(n);
                          }}
                          className="w-14 shrink-0 text-center border border-gray-200 rounded h-7 text-xs"
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

          {showActivityPolylines && activitySources.length > 0
            && activitySettings?.mode !== 'heatmap'
            && onActivityLineWidthChange && (() => {
              const INTERNAL_MIN = 0.01;
              const INTERNAL_MAX = 5;
              const DISPLAY_MIN = 1;
              const DISPLAY_MAX = 100;
              const internalToDisplay = (v) => {
                const ratio = (v - INTERNAL_MIN) / (INTERNAL_MAX - INTERNAL_MIN);
                return Math.round(DISPLAY_MIN + ratio * (DISPLAY_MAX - DISPLAY_MIN));
              };
              const displayToInternal = (d) => {
                const ratio = (d - DISPLAY_MIN) / (DISPLAY_MAX - DISPLAY_MIN);
                const v = INTERNAL_MIN + ratio * (INTERNAL_MAX - INTERNAL_MIN);
                // Round to 2 decimals so settings JSON stays readable and the
                // saved value comes back identical after a round-trip.
                return Math.round(v * 100) / 100;
              };

              const raw = activitySettings?.lineWidth;
              // Accept legacy named widths for back-compat with older saved
              // settings; everything else falls back to a sane new-user default.
              const current = typeof raw === 'number'
                ? raw
                : raw === 'very thin' ? 0.5
                  : raw === 'thin' ? 0.75
                    : raw === 'thick' ? 3
                      : raw === 'very thick' ? 5
                        : raw === 'very very thick' ? 7
                          : 0.8;
              const currentDisplay = internalToDisplay(
                Math.max(INTERNAL_MIN, Math.min(INTERNAL_MAX, current))
              );

              // Suggested width tracks how busy the map is: a single track
              // can be bold without losing context, while crowded views need
              // hairlines to keep individual paths distinguishable. The N^0.4
              // curve falls off slower than √N so 1000-activity maps still
              // get a usable thickness; 0.05 floor keeps lines visible. Count
              // is the unhidden polylines so panel toggles affect it.
              const visibleCount = visibleActivityCount;
              const suggested = visibleCount <= 0
                ? 0.8
                : Math.max(0.05, Math.round((2.4 / Math.pow(visibleCount, 0.4)) * 100) / 100);
              const suggestedDisplay = internalToDisplay(suggested);

              const setInternal = (v) => {
                const clamped = Math.max(INTERNAL_MIN, Math.min(INTERNAL_MAX, v));
                onActivityLineWidthChange(Math.round(clamped * 100) / 100);
              };
              // Manual edits drop out of auto mode so the user's choice sticks.
              const setManualValue = (v) => {
                if (lineWidthAuto && onActivityLineWidthAutoChange) {
                  onActivityLineWidthAutoChange(false);
                }
                setInternal(v);
              };
              const enableAuto = () => {
                if (onActivityLineWidthAutoChange) onActivityLineWidthAutoChange(true);
                setInternal(suggested);
              };

              return (
                <div className="relative" ref={lineWidthRef}>
                  <button
                    type="button"
                    onClick={() => {
                      closeOtherPopups('lineWidth');
                      setLineWidthPopupOpen((v) => !v);
                    }}
                    title={lineWidthAuto ? "Auto width — click to change" : "Adjust activity line width"}
                    className={`rounded shadow-md px-2.5 py-1 lg:px-4 lg:py-2.5 border border-gray-200 text-xs lg:text-sm font-medium flex items-center gap-0.5 lg:gap-1.5 ${lineWidthPopupOpen ? 'bg-gray-900 text-white' : 'bg-white text-gray-800 hover:bg-gray-100'} ${flashClass('width')}`}
                  >
                    <Minus size={14} className="lg:hidden" strokeWidth={Math.min(4, Math.max(1, current))} />
                    <Minus size={17} className="hidden lg:block" strokeWidth={Math.min(4, Math.max(1, current))} />
                    <span className="flex flex-col leading-tight text-left">
                      <span>{lineWidthAuto ? 'Width Auto' : `Width ${currentDisplay}`}</span>
                      <span className={`text-[9px] lg:text-[10px] font-normal ${lineWidthPopupOpen ? 'text-gray-300' : 'text-gray-500'}`}>
                        Opacity {Math.round(Math.max(0, Math.min(1, typeof activitySettings?.alpha === 'number' ? activitySettings.alpha : 1)) * 100)}%
                      </span>
                    </span>
                  </button>
                  {lineWidthPopupOpen && (
                    <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 p-3 space-y-3 z-[1100]">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-medium text-gray-700">
                          Activity line width <span className="text-gray-500 font-normal">({lineWidthAuto ? 'auto' : 'manual'})</span>
                        </div>
                        {onActivityLineWidthAutoChange && (
                          lineWidthAuto ? (
                            <button
                              type="button"
                              onClick={() => onActivityLineWidthAutoChange(false)}
                              className="text-[11px] px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-100"
                            >
                              Switch to manual
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={enableAuto}
                              className="text-[11px] px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-100"
                            >
                              Switch to auto
                            </button>
                          )
                        )}
                      </div>
                      {lineWidthAuto ? (
                        <div className="text-[11px] text-gray-600">
                          Width is set automatically based on how many activities are visible.
                          {visibleCount > 0 && (
                            <> Currently <span className="font-semibold text-gray-800">{suggestedDisplay}</span> for {visibleCount} {visibleCount === 1 ? 'activity' : 'activities'}.</>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min={DISPLAY_MIN}
                              max={DISPLAY_MAX}
                              step={1}
                              value={currentDisplay}
                              onChange={(e) => setManualValue(displayToInternal(parseInt(e.target.value, 10)))}
                              className="flex-1 min-w-0"
                            />
                            <input
                              type="number"
                              min={DISPLAY_MIN}
                              max={DISPLAY_MAX}
                              step={1}
                              value={currentDisplay}
                              onChange={(e) => {
                                const n = parseInt(e.target.value, 10);
                                if (!isNaN(n) && n >= DISPLAY_MIN && n <= DISPLAY_MAX) {
                                  setManualValue(displayToInternal(n));
                                }
                              }}
                              className="w-14 shrink-0 text-center border border-gray-200 rounded h-7 text-xs"
                            />
                          </div>
                          <div className="grid grid-cols-3 gap-1">
                            <button
                              type="button"
                              onClick={() => setManualValue(1.2)}
                              className="text-[11px] px-2 py-1 rounded border border-gray-200 hover:bg-gray-100"
                              title="Best for 1 activity"
                            >
                              Single
                            </button>
                            <button
                              type="button"
                              onClick={() => setManualValue(0.3)}
                              className="text-[11px] px-2 py-1 rounded border border-gray-200 hover:bg-gray-100"
                              title="Good for ~10 activities"
                            >
                              Some
                            </button>
                            <button
                              type="button"
                              onClick={() => setManualValue(0.05)}
                              className="text-[11px] px-2 py-1 rounded border border-gray-200 hover:bg-gray-100"
                              title="Best for 100+ activities"
                            >
                              Many
                            </button>
                          </div>
                        </>
                      )}
                      {onActivityAlphaChange && (() => {
                        const rawAlpha = activitySettings?.alpha;
                        const currentAlpha = typeof rawAlpha === 'number' ? rawAlpha : 1;
                        const displayAlpha = Math.round(Math.max(0, Math.min(1, currentAlpha)) * 100);
                        const setAlphaPct = (pct) => {
                          const clamped = Math.max(0, Math.min(100, pct));
                          onActivityAlphaChange(Math.round(clamped) / 100);
                        };
                        return (
                          <div className="pt-2 border-t border-gray-100 space-y-1">
                            <div className="text-xs font-medium text-gray-700">Opacity ({displayAlpha}%)</div>
                            <div className="flex items-center gap-2">
                              <input
                                type="range"
                                min={0}
                                max={100}
                                step={1}
                                value={displayAlpha}
                                onChange={(e) => setAlphaPct(parseInt(e.target.value, 10))}
                                className="flex-1 min-w-0"
                              />
                              <input
                                type="number"
                                min={0}
                                max={100}
                                step={1}
                                value={displayAlpha}
                                onChange={(e) => {
                                  const n = parseInt(e.target.value, 10);
                                  if (!isNaN(n)) setAlphaPct(n);
                                }}
                                className="w-14 shrink-0 text-center border border-gray-200 rounded h-7 text-xs"
                              />
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })()}

        </div>

        {/* Zoom-to-rectangle: sits just below leaflet's built-in +/- zoom
            control (top-left, ~70px below the top edge). */}
        <button
          type="button"
          onClick={handleZoomToRectangle}
          title="Zoom to rectangle"
          aria-label="Zoom to rectangle"
          className={`absolute top-[78px] left-[10px] z-[1000] bg-white hover:bg-gray-100 rounded shadow-md p-1.5 lg:p-2.5 border border-gray-200 ${flashClass('rectangle')}`}
        >
          <RectangleHorizontalIcon size={17} className="text-blue-600 lg:hidden" />
          <RectangleHorizontalIcon size={22} className="text-blue-600 hidden lg:block" />
        </button>

        {/* Top-center: location search trigger + popup. Lets the user change
            the map's place without leaving the map. */}
        {onPlaceNameChange && (
          <div ref={locationRef} className="absolute top-2 lg:top-4 left-1/2 -translate-x-1/2 z-[1000] flex items-center">
            {/* Location pill: left region opens the search popup, right
                region (only when a place is set) centers the map on that
                place. Separator keeps the two actions visually distinct. */}
            <div className={`flex items-stretch rounded shadow-md border border-gray-200 overflow-hidden max-w-[200px] lg:max-w-[260px] ${locationPopupOpen ? 'bg-gray-900' : 'bg-white'} ${flashClass('location')}`}>
              <button
                type="button"
                onClick={() => {
                  closeOtherPopups('location');
                  setLocationPopupOpen((v) => !v);
                }}
                title="Search location"
                className={`flex items-center gap-1 px-2.5 py-1 lg:px-4 lg:py-2.5 text-xs lg:text-sm font-medium min-w-0 flex-1 ${locationPopupOpen ? 'text-white hover:bg-gray-800' : 'text-gray-800 hover:bg-gray-100'}`}
              >
                <MapPin size={14} className="lg:hidden" />
                <MapPin size={17} className="hidden lg:block" />
                <span className="truncate">
                  {/* Show the active placeName only while the user is mid-
                      selection (still typing OR they've picked a result and
                      not yet centered). After the center button has been
                      clicked the visible label resets to "Search location",
                      even though placeName itself stays around for the
                      print label and recent_images entry. */}
                  {(pendingCenter || searchInput) && placeName ? placeName : 'Search location'}
                </span>
              </button>
              {/* Apply button moved into the popup, below the input, where
                  it's prominent and explicit. The pill itself stays single-
                  purpose (open the popup). */}
            </div>
            {locationPopupOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 p-3 space-y-3 z-[1100]">
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => {
                      setSearchInput(e.target.value);
                      onPlaceNameChange(e);
                    }}
                    placeholder="Search a place…"
                    autoFocus
                    className={`w-full h-10 pl-9 border-2 border-gray-200 focus:border-blue-500 bg-gray-50 focus:bg-white rounded-xl text-sm outline-none ${(searchInput || placeName) ? 'pr-9' : 'pr-3'}`}
                  />
                  {(searchInput || placeName) && onClearPlace && (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setSearchInput(''); onClearPlace(); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-200"
                      aria-label="Clear location"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                {/* Big, blinking Apply button anchored under the input so
                    the user can't miss it once they've picked a place. Only
                    shows while a selection is pending; clicking it centers
                    the map, clears the pending state, and closes the popup. */}
                {pendingCenter && onCenterPlace && (
                  <button
                    type="button"
                    onClick={() => {
                      onCenterPlace();
                      setPendingCenter(false);
                      setSearchInput('');
                      setLocationPopupOpen(false);
                    }}
                    className="w-full h-10 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold flex items-center justify-center gap-2 shadow-md ring-2 ring-blue-400 animate-pulse"
                  >
                    <ArrowRight className="h-4 w-4" />
                    Apply location
                  </button>
                )}

                {searchResults.length > 0 && (
                  <div className="border border-gray-200 rounded-xl max-h-52 overflow-y-auto">
                    {searchResults.map((result, index) => (
                      <div
                        key={index}
                        className="px-3 py-1.5 text-sm hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 truncate"
                        title={result.display_name}
                        onClick={() => {
                          if (onSelectResult) onSelectResult(result);
                          setPendingCenter(true);
                        }}
                      >
                        {result.display_name}
                      </div>
                    ))}
                  </div>
                )}

                {recentPlaces.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">Recent</div>
                    <div className="flex flex-wrap gap-1.5">
                      {recentPlaces.map((name) => (
                        <Button
                          key={name}
                          type="button"
                          onClick={() => {
                            if (onSelectRecent) onSelectRecent(name);
                            setPendingCenter(true);
                          }}
                          className="px-2.5 py-1 text-xs rounded-full border border-gray-300 bg-white hover:bg-gray-100 text-gray-800 h-auto truncate max-w-full"
                          title={name}
                          variant="ghost"
                        >
                          {name.split(',')[0]}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}


        {/* Style switcher — bottom-left so it doesn't overlap Leaflet's top-left zoom controls. */}
        {onMapStyleChange && (
          <div className="absolute bottom-2 left-2 lg:bottom-4 lg:left-4 bg-white rounded shadow-md flex text-xs z-[1000] overflow-hidden">
            {[
              { id: 'standard', label: 'Map' },
              { id: 'topo', label: 'Topo' },
              { id: 'satellite', label: 'Satellite' },
            ].map(option => (
              <button
                key={option.id}
                type="button"
                onClick={() => onMapStyleChange(option.id)}
                className={`px-2.5 py-1 lg:px-4 lg:py-1.5 text-sm lg:text-base border-r last:border-r-0 ${activeStyle === option.id ? 'bg-gray-900 text-white' : 'hover:bg-gray-100'}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MapComponent;
